import React from "react";
import { Check, ChevronRight, MoreHorizontal, Search } from "lucide-react";
import { Button } from "./Button";
import { Badge } from "./Badge";
export function StatusBadge({
  status
}: {
  status: string;
}) {
  const styles: Record<string, string> = {
    "আবেদন চলছে": "bg-emerald-50 text-emerald-800 border-emerald-200",
    "যাচাই চলছে": "bg-amber-50 text-amber-800 border-amber-200",
    "মূল্যায়ন চলছে": "bg-violet-50 text-violet-800 border-violet-200",
    "মূল্যায়নাধীন": "bg-violet-50 text-violet-800 border-violet-200",
    "শর্টলিস্টেড": "bg-sky-50 text-sky-800 border-sky-200",
    "যাচাই অপেক্ষমাণ": "bg-amber-50 text-amber-800 border-amber-200",
    "নির্বাচিত": "bg-emerald-50 text-emerald-800 border-emerald-200",
    "সংশোধন প্রয়োজন": "bg-rose-50 text-rose-800 border-rose-200",
    "প্রত্যাখ্যাত": "bg-rose-50 text-rose-800 border-rose-200"
  };
  return <Badge variant="outline" className={`whitespace-nowrap rounded-md px-2 py-1 font-medium ${styles[status] || "bg-slate-50 text-slate-700"}`} data-id="element-308" data-mp="29wahlhhx219j">{status}</Badge>;
}
export function PageTitle({
  title,
  eyebrow,
  actions
}: {
  title: string;
  eyebrow?: string;
  actions?: React.ReactNode;
}) {
  return <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end" data-id="element-309" data-mp="3cgaxnt99qdk8"><div data-id="element-310" data-mp="3heces8n4w2t2"><div className="mb-2 flex items-center gap-1.5 text-xs text-[#667085]" data-id="element-311" data-mp="3hbgieoqxpm6q"><span data-id="element-312" data-mp="3mbb9otemgdsb">প্রশাসন</span><ChevronRight className="h-3 w-3" data-id="element-313" data-mp="3mbussdg0d87x" /><span data-id="element-314" data-mp="2oovu5x5r9q7e">{eyebrow || title}</span></div><h1 className="text-2xl font-bold tracking-tight text-[#1F2937]" data-id="element-315" data-mp="2opx4u0iv0aol">{title}</h1></div>{actions && <div className="flex flex-wrap gap-2" data-id="element-316" data-mp="2tn6yjsda78i8">{actions}</div>}</div>;
}
export function SearchBox({
  placeholder = "খুঁজুন"
}: {
  placeholder?: string;
}) {
  return <div className="relative" data-id="element-317" data-mp="2yl8iefij7bur"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#667085]" data-id="element-318" data-mp="33ea2d1xli1l7" /><input aria-label={placeholder} className="h-10 w-full rounded-lg border border-[#D9E2DE] bg-white pl-10 pr-3 text-sm outline-none transition focus:border-[#006A4E] focus:ring-2 focus:ring-[#006A4E]/15" placeholder={placeholder} data-id="element-319" data-mp="25yw8zld1cv99" /></div>;
}
export function EmptyState({
  title = "কোনো তথ্য পাওয়া যায়নি",
  text = "ফিল্টার পরিবর্তন করে আবার চেষ্টা করুন।"
}: {
  title?: string;
  text?: string;
}) {
  return <div className="rounded-xl border border-dashed border-[#D9E2DE] bg-white px-6 py-12 text-center" data-id="element-320" data-mp="2cn46uvoeg249"><div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#EAF5F1]" data-id="element-321" data-mp="27mzqr6rel9ek"><Search className="h-5 w-5 text-[#006A4E]" data-id="element-322" data-mp="1jxdq0evdum4g" /></div><p className="font-semibold text-[#1F2937]" data-id="element-323" data-mp="1jy72zxkxq7v1">{title}</p><p className="mt-1 text-sm text-[#667085]" data-id="element-324" data-mp="1fwtxxppo28p6">{text}</p></div>;
}
export function MetricCard({
  label,
  value,
  icon: Icon,
  tone = "green"
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  tone?: "green" | "red" | "amber" | "blue";
}) {
  const tones = {
    green: "bg-[#EAF5F1] text-[#006A4E]",
    red: "bg-rose-50 text-[#C61E35]",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-sky-50 text-sky-700"
  };
  return <article className="rounded-xl border border-[#D9E2DE] bg-white p-4 shadow-sm" data-id="element-325" data-mp="30cyi58t2lime"><div className="flex items-start justify-between" data-id="element-326" data-mp="m4nuz2f73fih"><p className="text-sm text-[#667085]" data-id="element-327" data-mp="m2uphqgbj1c2">{label}</p><div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`} data-id="element-328" data-mp="r38z3cxkijc7"><Icon className="h-4 w-4" data-id="element-329" data-mp="d3ppsgt0t1yb" /></div></div><p className="mt-4 text-2xl font-bold text-[#1F2937]" data-id="element-330" data-mp="d4rdplv4xixg">{value}</p></article>;
}
export function SectionHeader({
  title,
  action,
  onAction
}: {
  title: string;
  action?: string;
  onAction?: () => void;
}) {
  return <div className="mb-4 flex items-center justify-between" data-id="element-331" data-mp="15tg70ypy8jyn"><h2 className="font-bold text-[#1F2937]" data-id="element-332" data-mp="19tyzmuzo1u1w">{title}</h2>{action && <Button variant="ghost" size="sm" onClick={onAction} className="text-[#006A4E] hover:bg-[#EAF5F1] hover:text-[#005A3C]" data-id="element-333" data-mp="19tgxjhqbolm2">{action}<ChevronRight className="ml-1 h-4 w-4" data-id="element-334" data-mp="w0r6uzzumsim" /></Button>}</div>;
}
export function MoreButton() {
  return <Button aria-label="আরও অপশন" size="icon-xs" variant="ghost" data-id="element-335" data-mp="10zbzad8v07wi"><MoreHorizontal className="h-4 w-4" data-id="element-336" data-mp="1oqhdyp32cu58" /></Button>;
}
export function Timeline({
  active = 4
}: {
  active?: number;
}) {
  const steps = ["আবেদন জমা", "প্রাথমিক যাচাই", "ডকুমেন্ট যাচাই", "শর্টলিস্ট", "জুরি মূল্যায়ন", "চূড়ান্ত অনুমোদন", "ফলাফল প্রকাশ"];
  return <ol className="space-y-0" data-id="element-337" data-mp="3w2g1hhbzlfch">{steps.map((step, i) => <li key={step} className="relative flex gap-3 pb-4 last:pb-0" data-id="element-338" data-mp="3w1oa6ipfa3s0" data-mpi={i}><div className={`relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] ${i < active ? "border-[#006A4E] bg-[#006A4E]" : i === active ? "border-[#006A4E] bg-[#EAF5F1]" : "border-[#D9E2DE] bg-white"}`} data-id="element-339" data-mp="3s0ck6c2mzgja">{i < active ? <Check className="h-3 w-3 text-white" data-id="element-340" data-mp="nluuu76tuycj" /> : i === active ? <span className="h-2 w-2 rounded-full bg-[#006A4E]" data-id="element-341" data-mp="jkswrtoncsc5" /> : null}</div>{i < steps.length - 1 && <span className={`absolute left-[9px] top-5 h-[calc(100%-12px)] w-px ${i < active ? "bg-[#006A4E]" : "bg-[#D9E2DE]"}`} data-id="element-342" data-mp="etswqfk8du5f" />}<span className={`text-sm ${i <= active ? "font-medium text-[#1F2937]" : "text-[#667085]"}`} data-id="element-343" data-mp="9u6okkj9f43h">{step}</span></li>)}</ol>;
}